import {
  addDoc,
  collection,
  serverTimestamp,
  onSnapshot,
  query,
  orderBy,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";

/**
 * Creates a new booking request in Firestore under the "bookings"
 * collection. Bookings start in "pending" status and are actioned
 * manually (call/text/email) until an admin review flow exists.
 *
 * NOTE: this only works once Firestore security rules allow
 * unauthenticated create on "bookings", and authenticated read/update
 * for the admin dashboard. Suggested rules:
 *
 *   match /bookings/{bookingId} {
 *     allow create: if request.resource.data.keys().hasAll(
 *       ['name','phone','service','preferredDate']
 *     );
 *     allow read, update: if request.auth != null;
 *     allow delete: if false;
 *   }
 */
export async function createBooking(booking) {
  const payload = {
    name: booking.name.trim(),
    phone: booking.phone.trim(),
    email: booking.email?.trim() || "",
    service: booking.service,
    preferredDate: booking.preferredDate,
    address: booking.address?.trim() || "",
    notes: booking.notes?.trim() || "",
    status: "pending",
    createdAt: serverTimestamp(),
  };

  const ref = await addDoc(collection(db, "bookings"), payload);
  return ref.id;
}

/**
 * Subscribes to all bookings, newest first, calling `onData` with the
 * full array on every change. Returns an unsubscribe function — call
 * it on unmount. `onErr` fires once if the read is denied (e.g. rules
 * not yet updated, or the admin isn't signed in).
 */
export function subscribeToBookings(onData, onErr) {
  const q = query(collection(db, "bookings"), orderBy("createdAt", "desc"));
  return onSnapshot(
    q,
    (snapshot) => {
      const bookings = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      onData(bookings);
    },
    onErr
  );
}

export function updateBookingStatus(id, status) {
  return updateDoc(doc(db, "bookings", id), { status });
}
